import { makeAutoObservable, type IReactionDisposer, reaction } from 'mobx'
import { nanoid } from 'nanoid'
import { AxiosError } from 'axios'
import { isEmail } from 'shared/lib'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type ReCaptchaStore } from 'entities/Auth'
import { TwoFaModalStore } from 'entities/Auth/ui/TwoFaModal'

export class LoginStore {
  private _email = ''
  private _password = ''
  private _loading = false
  private _error = ''
  private _alertErrors: string[] = []
  private _alertSuccess = ''
  private _alertWarning = ''

  // Email validation
  private _disposeEmailValidation: IReactionDisposer | null = null
  private _disposeEmailValidationTrigger: IReactionDisposer | null = null
  private _emailError: string | null = null
  private _emailValidationTrigger: string | null = null
  private _showConstantEmailError = false

  // Password validation
  private _disposePasswordValidation: IReactionDisposer | null = null
  private _disposePasswordValidationTrigger: IReactionDisposer | null = null
  private _passwordError: string | null = null
  private _passwordValidationTrigger: string | null = null
  private _showConstantPasswordError = false

  private readonly _twoFaModalStore: TwoFaModalStore

  constructor(
    private _entities: IEntitiesStore,
    private _storeReCaptchaStore: ReCaptchaStore,
    initialEmail?: string
  ) {
    this._twoFaModalStore = new TwoFaModalStore(this._entities.authStore)

    if (initialEmail) {
      this._email = initialEmail
    }

    makeAutoObservable(this)
  }

  initReactions = () => {
    this.reactionEmailValidation()
    this.reactionPasswordValidation()
    this.reactionEmailValidationTrigger()
    this.reactionPasswordValidationTrigger()
  }

  disposeReaction = () => {
    this._disposeEmailValidation?.()
    this._disposeEmailValidationTrigger?.()
    this._disposePasswordValidation?.()
    this._disposePasswordValidationTrigger?.()
  }

  init = () => {
    this.initReactions()
  }

  dispose = () => {
    this.disposeReaction()
  }

  get storeReCaptchaStore() {
    return this._storeReCaptchaStore
  }

  get showConstantEmailError() {
    return this._showConstantEmailError
  }

  get showConstantPasswordError() {
    return this._showConstantPasswordError
  }

  get alertSuccess() {
    return this._alertSuccess
  }

  get alertWarning() {
    return this._alertWarning
  }

  get isValidEmail() {
    return isEmail(this._email)
  }

  get hasAlertErrors() {
    return !!this.alertErrors.length
  }

  get alertErrors() {
    return this._alertErrors
  }

  get hasEmail() {
    return !!this.email
  }

  get hasPassword() {
    return !!this.password
  }

  get email() {
    return this._email
  }

  get password() {
    return this._password
  }

  get loading() {
    return this._loading
  }

  get emailError() {
    return this._emailError
  }

  get passwordError() {
    return this._passwordError
  }

  get error() {
    return this._error
  }

  get deviceId() {
    return localStorage.getItem('device_id')
  }

  showConstantErrors = () => {
    this._showConstantEmailError = true
    this._showConstantPasswordError = true
  }

  clearConstantErrors = () => {
    this._showConstantEmailError = false
    this._showConstantPasswordError = false
  }

  setAlertSuccess = (value: string) => {
    this._alertSuccess = value
  }

  setAlertWarning = (value: string) => {
    this._alertWarning = value
  }

  clearAlertErrors = () => {
    if (this.hasAlertErrors) this._alertErrors = []
  }

  setLoading = (value: boolean) => {
    this._loading = value
  }

  setEmail = (value: string) => {
    this.clearAlertErrors()
    this._email = value.trim()
  }

  setPassword = (value: string) => {
    this.clearAlertErrors()
    this._password = value
  }

  triggerEmailValidation = () => {
    this._emailValidationTrigger = nanoid()
  }

  triggerPasswordValidation = () => {
    this._passwordValidationTrigger = nanoid()
  }

  signIn = async () => {
    this.clearAlertErrors()

    if (!this.hasPassword || !this.isValidEmail) {
      this.triggerEmailValidation()
      this.triggerPasswordValidation()
      return
    }

    try {
      this.clearConstantErrors()
      this.setLoading(true)

      await this._storeReCaptchaStore.setExecuteRecaptcha('2fa_auth_login')

      const data = await this._entities.authStore.login(
        {
          email: this.email,
          password: this.password,
          device_id: this.deviceId,
          ...this._storeReCaptchaStore.params,
        },
        {
          reCaptchaStore: this._storeReCaptchaStore,
        }
      )

      if (data) {
        this._twoFaModalStore.openTwoFaModal({
          settings: data.settings,
          owner_email_settings: data.owner_email_settings,
        })
      }
    } catch (e) {
      if (e instanceof AxiosError) {
        if (this._storeReCaptchaStore.checkServerError(e.errors.getError('message'))) return

        e.errors.values().forEach((msg) => this._alertErrors.push(msg.value))
      }
    } finally {
      this.setLoading(false)
    }
  }

  reactionEmailValidation = () => {
    this._disposeEmailValidation?.()
    this._disposeEmailValidation = reaction(
      () => [this._emailValidationTrigger, this.isValidEmail],
      ([, isValid]) => {
        if (isValid) {
          this._emailError = null
          this._showConstantEmailError = false
        }
      },
      {
        fireImmediately: true,
      }
    )
  }

  reactionEmailValidationTrigger = () => {
    this._disposeEmailValidationTrigger?.()
    this._disposeEmailValidationTrigger = reaction(
      () => this._emailValidationTrigger,
      (value) => {
        this._emailError =
          value && !this.isValidEmail
            ? this.hasEmail
              ? 'Please enter a valid email address.'
              : 'Please enter email address.'
            : null
        if (value && this.isValidEmail) {
          this._emailValidationTrigger = null
          if (document.activeElement instanceof HTMLElement) document.activeElement.blur()
        }
      },
      {
        fireImmediately: true,
      }
    )
  }

  reactionPasswordValidation = () => {
    this._disposePasswordValidation?.()
    this._disposePasswordValidation = reaction(
      () => this.hasPassword,
      (value) => {
        if (value) {
          this._passwordError = null
          this._showConstantPasswordError = false
        }
      },
      {
        fireImmediately: true,
      }
    )
  }

  reactionPasswordValidationTrigger = () => {
    this._disposePasswordValidationTrigger?.()
    this._disposePasswordValidationTrigger = reaction(
      () => this._passwordValidationTrigger,
      (value) => {
        this._passwordError = value && !this.hasPassword ? 'Please enter password.' : null
        if (this.hasPassword) {
          this._passwordValidationTrigger = null
          if (document.activeElement instanceof HTMLElement) document.activeElement.blur()
        }
      },
      {
        fireImmediately: true,
      }
    )
  }
}
